import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ApiCall from "../../Apicall/ApiCall";
import { postLoginRedirect } from "../../utils/postLoginRedirect";
import PublicPage from "../../public/PublicPage";
import CompleteProfileForm from "../../public/components/ds/CompleteProfileForm";
import { dsToast } from "../../public/utils/dsToast";
import { useBankIdVerification } from "../../public/components/auth/BankIdVerification";

// Presentation follows the reference AccountSetup. Profile fetching, the
// update payload, and post-login routing remain owned by the main application.
const notify = dsToast;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;

function CompleteProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // The QR-invite flow (BuyerInviteFlow) sends a brand-new email/password
  // signup here to fill in their real name/phone before carrying on — it
  // passes where to go next so this page can resume that journey instead of
  // falling back to the normal post-login routing below.
  const continueTo = location.state?.continueTo;
  const continueState = location.state?.continueState;

  // Account setup starts in Swedish by default, matching the reference flow —
  // but ONLY when no language preference exists yet. Without the
  // `getItem` guard this ran unconditionally on every fire, and this effect
  // fires more than once per mount (StrictMode's double-invoke in dev, and
  // again on the re-render languageChanged triggers). Since it unconditionally
  // rewrote "appLanguage" back to "sv" every time, choosing English in the
  // header's language dropdown was reverted within ~50ms of the click — the
  // switcher looked broken because its own choice never survived to paint.
  // Once ANY preference is stored (by this default or a real user choice),
  // this effect becomes a no-op for the rest of the page's life.
  useLayoutEffect(() => {
    if (localStorage.getItem("appLanguage")) return;
    localStorage.setItem("appLanguage", "sv");
    if (!i18n.resolvedLanguage?.startsWith("sv")) i18n.changeLanguage("sv");
  }, [i18n]);

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isBankId, setIsBankId] = useState(false);
  const [bankIdSSN, setBankIdSSN] = useState("");
  const [headerDisplayName, setHeaderDisplayName] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    hasValidLicense: null,
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  // An e-mail sign-up may link BankID here already (optional; it is asked for before a deal).
  const { ensureVerified, verificationDialogs } = useBankIdVerification();
  const [emailValid, setEmailValid] = useState(null);
  const [phoneValid, setPhoneValid] = useState(null);

  // ----- load current user & prefill -----
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const sessionRes = await ApiCall({
          url: "/Session/GetCurrentLoginInformations",
          method: "GET",
        });
        const sessionUser = sessionRes?.data?.result?.user || {};
        const uid = sessionUser.id;
        if (!uid) {
          navigate("/Home", { replace: true });
          return;
        }
        if (!active) return;

        const profRes = await ApiCall({
          url: "/User/GetUserProfileById",
          method: "GET",
          params: { Id: uid },
        });
        const u = profRes?.data?.result || {};
        if (!active) return;

        // Email signups seed name/surname with the email address as a
        // placeholder — don't show that in the name fields, let the user type.
        // The comparison must NOT be an exact match: the backend runs the seeded
        // value through NameFormatter, which capitalises it
        // ("kashsaee100@gmail.com" -> "Kashsaee100@gmail.com"), so an exact
        // match silently stopped catching the placeholder and the email was
        // rendered into the name inputs. Case/whitespace are ignored, and any
        // value containing "@" is a placeholder whatever the current email is —
        // a person's name never contains one, and the user may have changed
        // their email since signing up.
        const isEmailPlaceholder = (val) => {
          const value = (val || "").trim();
          if (!value) return false;
          if (value.includes("@")) return true;
          const email = (u.emailAddress || "").trim();
          return email !== "" && value.toLowerCase() === email.toLowerCase();
        };
        // A real BankID signup has BOTH a personnummer (digits, not the
        // "string" placeholder email signups seed) AND a real name — BankID
        // itself supplies the legal name at auth time, so a genuine BankID
        // account can never still have its name sitting at the raw email
        // placeholder. A record with a personnummer-shaped BankIdSSN but a
        // still-placeholder name is a stub (e.g. seeded directly in the
        // database) that still needs the name fields, not a verified BankID
        // identity that should hide them.
        const ssn = u.bankIdSSN || "";
        const hasRealName = !isEmailPlaceholder(u.name) && !isEmailPlaceholder(u.surname) && !!u.name && !!u.surname;
        const bankId = (u.isBankIdVerified ?? (ssn !== "" && ssn.toLowerCase() !== "string" && /\d/.test(ssn))) && hasRealName;
        setIsBankId(bankId);
        setBankIdSSN(bankId ? ssn : "");
        const profileName = [u.name, u.surname]
          .filter((value) => value && !isEmailPlaceholder(value))
          .join(" ")
          .trim();
        const sessionName = [sessionUser.name, sessionUser.surname]
          // Same placeholder rule as above: the session copy of the name can be the
          // formatted email too, so drop anything that looks like an address.
          .filter((value) => value && !String(value).includes("@") && value !== sessionUser.emailAddress)
          .join(" ")
          .trim();
        setHeaderDisplayName(profileName || sessionName);
        setForm((prev) => ({
          ...prev,
          firstName: isEmailPlaceholder(u.name) ? "" : u.name || "",
          lastName: isEmailPlaceholder(u.surname) ? "" : u.surname || "",
          email: u.emailAddress || "",
          phoneNumber: u.personalContact || "",
          hasValidLicense:
            u.validCarLicense === true ? true : prev.hasValidLicense,
        }));
        if (u.emailAddress) setEmailValid(EMAIL_RE.test(u.emailAddress));
        if (u.personalContact) setPhoneValid(PHONE_RE.test(u.personalContact));
      } catch (e) {
        // not authenticated / expired → back to home
        navigate("/Home", { replace: true });
      } finally {
        if (active) setPageLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Once linked, the account carries the BankID name and personnummer: show the page as the BankID
  // version (name locked) while keeping whatever the user has typed in the other fields.
  const verifyBankId = async () => {
    if (!(await ensureVerified({ explain: false }))) return;
    try {
      const sessionRes = await ApiCall({ url: "/Session/GetCurrentLoginInformations", method: "GET" });
      const uid = sessionRes?.data?.result?.user?.id;
      const profRes = await ApiCall({ url: "/User/GetUserProfileById", method: "GET", params: { Id: uid } });
      const u = profRes?.data?.result || {};
      setIsBankId(true);
      setBankIdSSN(u.bankIdSSN || "");
      setForm((prev) => ({ ...prev, firstName: u.name || prev.firstName, lastName: u.surname || prev.lastName }));
      setHeaderDisplayName([u.name, u.surname].filter(Boolean).join(" "));
    } catch (_) {
      setIsBankId(true);   // linked; the name shows as it is the next time the page loads
    }
  };

  const onEmailChange = (v) => {
    setField("email", v);
    setEmailValid(v.length > 0 ? EMAIL_RE.test(v) : null);
  };
  const onPhoneChange = (v) => {
    setField("phoneNumber", v);
    setPhoneValid(v.length > 0 ? PHONE_RE.test(v) : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Same form for both sign-ups; a BankID account may leave the e-mail empty (checked if given).
    const emailOk = isBankId && !form.email.trim() ? true : EMAIL_RE.test(form.email);
    const phoneOk = PHONE_RE.test(form.phoneNumber);
    setEmailValid(emailOk);
    setPhoneValid(phoneOk);

    if (!form.firstName.trim() || !form.lastName.trim() || !emailOk || !phoneOk) {
      notify("error", {
        title: t("accountSetup.invalidTitle"),
        text: t("accountSetup.invalidText"),
      });
      return;
    }
    if (!termsAccepted) {
      notify("error", {
        title: t("accountSetup.termsTitle"),
        text: t("accountSetup.termsText"),
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.firstName.trim(),
        surname: form.lastName.trim(),
        // An empty e-mail keeps a BankID account without one (the server allows that only there).
        emailAddress: form.email.trim() || null,
        personalContact: form.phoneNumber.trim(),
        validCarLicense: form.hasValidLicense === true,
      };

      const res = await ApiCall({
        url: "/User/UpdateMyProfile",
        method: "PUT",
        data: payload,
      });

      const ok = res?.data?.success ?? res?.status === 200;
      if (!ok) throw new Error("save failed");

      notify("success", {
        title: t("accountSetup.successTitle"),
        text: t("accountSetup.successText", { name: form.firstName.trim() }),
      }, { position: "bottom-right" });

      // profile is now complete → resume wherever this journey was headed
      // (the QR-invite flow's waiting screen), or fall back to normal
      // post-login routing when there's nothing to resume.
      if (continueTo) navigate(continueTo, { replace: true, state: continueState });
      else await postLoginRedirect(navigate);
    } catch (err) {
      notify("error", {
        title: t("accountSetup.errorTitle"),
        text: t("accountSetup.errorText"),
      });
    } finally {
      setSaving(false);
    }
  };

  const displayName = form.firstName || t("accountSetup.there");

  // The header identity stays whatever the database already has (email for a
  // fresh signup, the real name once BankID or a saved profile has one) for
  // the whole time this page is open. It must NOT react to keystrokes in the
  // firstName/lastName fields below — that switch only happens on the next
  // page, once the profile is actually saved and re-fetched.
  return (
    <PublicPage>
      <CompleteProfileForm
        {...{ t, pageLoading, saving, isBankId, bankIdSSN, form, displayName, headerDisplayName,
          termsAccepted, setTermsAccepted, emailValid, phoneValid,
          setField, onEmailChange, onPhoneChange, handleSubmit }}
        onVerifyBankId={isBankId ? undefined : verifyBankId}
      />
      {verificationDialogs}
    </PublicPage>
  );
}

export default CompleteProfile;
